import {ReactMarkdownProps} from 'react-markdown/lib/ast-to-react';
import cx from 'classnames';
import Image from 'next/image';

import {Text} from '#component/base';
import {Markdown} from '#component/partial/Markdown';
import {ListItem} from '#component/partial/Markdown/component/ListItem';
import {Paragraph} from '#component/partial/Markdown/component/Paragraph';
import {ImpactLevel, ImpactLevelMap} from '#constant/impactLevel';
import {sprinkles} from '#style/sprinkles';

import {HealthFactorExpandedCardProps} from './HealthFactorExpandedCard.types';

import * as css from './HealthFactorExpandedCard.css';

/**
 * This component renders an expanded variant of the HealthFactor Card UI.
 */
export const HealthFactorExpandedCard = <T extends React.ElementType>({
  category,
  className,
  headline,
  imageUrl,
  impact = ImpactLevel.low,
  practicing,
  tips = '',
  ...otherProps
}: HealthFactorExpandedCardProps<T>) => (
  <Text as="div" className={cx(css.root, className)} {...otherProps}>
    <Text as="div" className={cx(css.header({impact, practicing}), className)}>
      {imageUrl && (
        <div className={css.image}>
          <Image aria-hidden alt="" src={imageUrl} height={156} width={156} />
        </div>
      )}

      <div className={sprinkles({dir: 'col', flex: 'flex', gap: 20, pos: 'relative'})}>
        <Text color="inherit" textAlign="left" variant="caption">
          {`${category ? `${category} · ` : ''}${ImpactLevelMap[impact]}`}
        </Text>

        <Text
          as="h4"
          className={sprinkles({pR: imageUrl ? 56 : 0})}
          color="inherit"
          fontWeight="semiBold"
          textAlign="left"
          variant="bodyLarge">
          {headline}
        </Text>
      </div>
    </Text>

    <div className={sprinkles({bg: 'ecruWhite', dir: 'col', flex: 'flex', gap: 16, p: [20, 28]})}>
      <Text color="mineShaft" fontWeight="semiBold">
        Tips & Tricks
      </Text>

      <Markdown
        className={sprinkles({flex: 'flex', dir: 'col', gap: 16})}
        components={{
          p: (props: ReactMarkdownProps) => <Paragraph {...props} />,
          li: (props: ReactMarkdownProps) => (
            <ListItem color="mineShaft" className={css.listItem} {...props} />
          ),
          ul: (props: ReactMarkdownProps) => <Text as="ul" {...props} />,
        }}
        data={tips || ''}
      />
    </div>
  </Text>
);
